import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);
import {
    DEFAULT_PAGE_SIZE,
    pascalToCamel,
    urlEndPoint,
    incentiveType,
    validateAiFadvLoginForm,
    resolveFadvIsOldForSubmit,
} from "helpers/GeneralHelper";
import {
    getAsyncFilterLabelFieldKey,
    isAsyncFilterLabelFieldKey,
    isMetadataAsyncSelectFilterDefinition,
    mergeModalSubmitIntoListFilter,
    normalizeMetadataFilterDateForApi,
} from "helpers/metadataFilterHelpers";
import { useMetadataDrivenFilters } from "hooks/useMetadataDrivenFilters";
import { useCommonTableSettingsPageSize } from "hooks/useSettingsReadyTablePageSize";

import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
    Form,
  Input,
    Label,
    Modal,
    ModalBody,
  Row,
} from "reactstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import BulkSelectionReviewModal from "pages/AllCommonModal/BulkSelectionReviewModal/bulk-selection-review-modal";
import EcommerceOrdersModal from "components/Common/EcommerceOrdersModal";
import Spinners from "components/Common/Spinner";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import { useClientRegisterContext } from "libs/context/ClientRegisterContext";
import {
    getClientRegisterFetchData,
    getClientRegisterFadvDetails,
    getCommonPlanOptions,
    mapCommonPlanApiToSelectOptions,
    submitClientRegisterBill,
    submitClientRegisterFilterData,
    submitClientRegisterIncentiveTarget,
    submitClientRegisterPermalink,
    updateClientRegisterAiFadvDetails,
    moveClientRegisterToAiRecruiterMultiple,
    disableClientRegisterAiRecruiterMultiple,
} from "model/ClientRegisterModel";
import { addOffboarding, getOffboardingByClientId } from "model/OffboardingModel";
import TableContainer from "components/Common/TableContainer";
import MetadataFilterToolbar from "components/Common/MetadataFilterToolbar";
import Loaders from "components/Common/Loaders";
import { usePermissionContext } from "libs/context/PermissionContext";

const CLIENT_REGISTER_PAGE_NAME = "ClientRegister";

const strOrNull = (v) => {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    return s === "" ? null : s;
};

const normalizeYesNoFilterValue = (value) => {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "boolean") return value;
    const normalized = String(value).trim().toLowerCase();
    if (normalized === "true" || normalized === "yes" || normalized === "1") return 'yes';
    if (normalized === "false" || normalized === "no" || normalized === "0") return 'no';
    return strOrNull(value);
};

const normalizeClientRegisterFilterPayload = (raw = {}, metadataFilters = []) => {
    const out = { ...raw };
    const dateFields = new Set(
        (Array.isArray(metadataFilters) ? metadataFilters : [])
            .filter((f) => f?.type === "date" && f?.field)
            .map((f) => f.field)
    );

    (Array.isArray(metadataFilters) ? metadataFilters : []).forEach((def) => {
        const field = def?.field;
        if (!field || !(field in out)) return;
        if (def?.type === "date" || dateFields.has(field)) {
            out[field] = normalizeMetadataFilterDateForApi(out[field]);
            return;
        }
        if (field === "isNegative" || def?.api === "get_yes_no") {
            out[field] = normalizeYesNoFilterValue(out[field]);
            return;
        }
        if (typeof out[field] === "string") {
            out[field] = strOrNull(out[field]);
        }
    });

    ["nameContains", "planId", "statusId", "isNegative", "fromDate", "toDate"].forEach(
        (key) => {
            if (!(key in out)) return;
            if (dateFields.has(key) || key === "fromDate" || key === "toDate") {
                out[key] = normalizeMetadataFilterDateForApi(out[key]);
            } else if (key === "isNegative") {
                out[key] = normalizeYesNoFilterValue(out[key]);
            } else if (typeof out[key] === "string") {
                out[key] = strOrNull(out[key]);
            }
        }
    );

    Object.keys(out).forEach((key) => {
        if (isAsyncFilterLabelFieldKey(key)) delete out[key];
    });
    delete out.search;
    return out;
};

const CLIENT_REGISTER_AI_RECRUITER_BULK_ACTIONS = [
    {
        value: "remove-from-ai-recruiter",
        label: "Remove From AI Recruiter",
        iconClass: "mdi mdi-robot-off",
        tone: "danger",
    },
    {
        value: "move-to-ai-recruiter",
        label: "Move To AI Recruiter",
        iconClass: "mdi mdi-robot",
    },
];

const ClientRegister = () => {
  const navigate = useNavigate();
    const { permissionData } = usePermissionContext() || {};
    const { fetchClientRegisterContextData, clientRegisterContextData } = useClientRegisterContext() || {};
    const { pageSize: settingsPageSize, settingsReady } = useCommonTableSettingsPageSize(
        CLIENT_REGISTER_PAGE_NAME
    );
    const initialFilterFetchedRef = useRef(false);
  const [modal1, setModal1] = useState(false);
    /** Filled from RegisterForm metadata filters (`nameContains`, `planId`, `statusId`, `isNegative`, dates). */
    const [filterForm, setFilterForm] = useState({});
    /** Incentive modal plan select only — filter Plan uses metadata `get_plan_options`. */
    const [planOptions, setPlanOptions] = useState([]);
    const [clientRegisterData, setClientRegisterData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableMetaData, setTableMetadata] = useState();
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [commonResetFilter, setCommonResetFilter] = useState(false);
  const [requestPayload, setRequestPayload] = useState({ pageNumber: 1, pageSize: DEFAULT_PAGE_SIZE });
  const [paginationMeta, setPaginationMeta] = useState({
    pageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages: 0,
    totalRecords: 0,
  });
    const [billModalOpen, setBillModalOpen] = useState(false);
    const [billItemId, setBillItemId] = useState("");
    const [billForm, setBillForm] = useState({ billingDate: "" });
    const [billErrors, setBillErrors] = useState({});
    const [billSubmitting, setBillSubmitting] = useState(false);
    const [permalinkModalOpen, setPermalinkModalOpen] = useState(false);
    const [permalinkItemId, setPermalinkItemId] = useState("");
    const [permalinkForm, setPermalinkForm] = useState({ permalink: "" });
    const [permalinkErrors, setPermalinkErrors] = useState({});
    const [permalinkSubmitting, setPermalinkSubmitting] = useState(false);
    const [offboardingModalOpen, setOffboardingModalOpen] = useState(false);
    const [offboardingItemId, setOffboardingItemId] = useState("");
    const [offboardingForm, setOffboardingForm] = useState({ lastWorkingDay: "", note: "" });
    const [offboardingErrors, setOffboardingErrors] = useState({});
    const [offboardingSubmitting, setOffboardingSubmitting] = useState(false);
    const [offboardingFetching, setOffboardingFetching] = useState(false);
    const [incentiveModalOpen, setIncentiveModalOpen] = useState(false);
    const [advantageLoginModalOpen, setAdvantageLoginModalOpen] = useState(false);
    const [incentiveItemId, setIncentiveItemId] = useState("");
    const [advantageLoginItemId, setAdvantageLoginItemId] = useState("");
    const [advantageLoginClientName, setAdvantageLoginClientName] = useState("");
    const [incentiveForm, setIncentiveForm] = useState({
        min: "",
        max: "",
        perAmount: "",
        isNegative: false,
        planId: "",
        maxQuota: "",
        quotaStartDate: "",
    });
    const [incentiveErrors, setIncentiveErrors] = useState({});
    const [incentiveSubmitting, setIncentiveSubmitting] = useState(false);
    const [advantageForm, setAdvantageForm] = useState({
        account: "",
        user: "",
        password: "",
        secret: "",
        isOtpBased: false,
        outlookEmail: "",
        userEmail: "",
        isOld: false,
        sameAsFirstAdvantage: false,
    });
    const [advantageErrors, setAdvantageErrors] = useState({});
    const [advantageSubmitting, setAdvantageSubmitting] = useState(false);
    const [advantageFetching, setAdvantageFetching] = useState(false);
    const [advantageSourceData, setAdvantageSourceData] = useState({
        fadv: {
            account: "",
            user: "",
            password: "",
            secret: "",
            isOtpBased: false,
            outlookEmail: "",
            userEmail: "",
            isOld: false,
        },
        aiFadv: {
            account: "",
            user: "",
            password: "",
            secret: "",
            isOtpBased: false,
            outlookEmail: "",
            userEmail: "",
            isOld: false,
        },
  });
  const [incentiveTypes, setIncentiveType] = useState(null);
  const [aiRecruiterSelectedClientsById, setAiRecruiterSelectedClientsById] = useState({});
  const [selectionReviewModalOpen, setSelectionReviewModalOpen] = useState(false);
  const toggleViewModal = () => setModal1(!modal1);

    const filteredData = useMemo(() => clientRegisterData, [clientRegisterData]);

    const registerActionPermissions = useMemo(
        () => ({
            canCreate: Boolean(permissionData?.RegisterAddAccess),
            canEdit: Boolean(permissionData?.RegisterUpdateAccess),
            canView: Boolean(
                permissionData?.RegisterAccess && !permissionData?.RegisterUpdateAccess
            ),
            canBill: Boolean(permissionData?.RegisterBillAccess),
            canPermalink: Boolean(permissionData?.RegisterPermalinkAccess),
            canOffboarding: Boolean(
                permissionData?.OffboardingAddAccess || permissionData?.OffboardingAccess
            ),
            canIncentiveTarget: Boolean(permissionData?.RegisterIncentiveAccess),
            canJobDescription: Boolean(
                permissionData?.RegisterJobDescriptionUpdateAccess ||
                    permissionData?.ClientJobDescViewAcces
            ),
            canAppreciation: Boolean(
                permissionData?.ClientAppreciationUpdateAccess ||
                    permissionData?.ClientAppreciationViewAcces
            ),
            canPreQuestioning: Boolean(
                permissionData?.RegisterPreQuestioningAccess ||
                    permissionData?.ClientPreQuestioningAccess
            ),
            canAdvantageLogin: Boolean(permissionData?.RegisterAIFADVAccess),
            canMoveToAiRecruiter: Boolean(permissionData?.RegisterUpdateAccess),
        }),
        [permissionData]
    );

    const canMoveToAiRecruiter = registerActionPermissions.canMoveToAiRecruiter;

    const hasAnyRegisterRowAction = useMemo(() => {
        return Object.values(registerActionPermissions).some(Boolean);
    }, [registerActionPermissions]);

    const {
        shouldShowTableHeaderFilterAndResetControls,
        shouldRenderMetadataToolbarFilterRow,
        activeModalFilterCount,
        modalFilterData: clientRegisterFilterModalData,
        metadataToolbarFilterDefinitions,
        metadataFiltersAll,
        usesMetadataDrivenFilterUi,
        buildBaselineEmptyListFilter,
        toolbarDropdownReactSelectOptionsByFieldName,
    } = useMetadataDrivenFilters(
        clientRegisterContextData?.filters,
        filterForm,
        CLIENT_REGISTER_PAGE_NAME
    );

    const formatDateToMMDDYYYY = (value) => {
        if (!value) return "";
        const parsedDate = dayjs(value);
        if (!parsedDate.isValid()) return value;
        return parsedDate.format("MM-DD-YYYY");
    };

    const formatDateForInput = (value) => {
        if (!value) return "";
        const stringValue = String(value).trim();
        const strictFormats = [
            "YYYY-MM-DD",
            "MM-DD-YYYY",
            "M-D-YYYY",
            "MM/DD/YYYY",
            "M/D/YYYY",
        ];
        for (const fmt of strictFormats) {
            const parsed = dayjs(stringValue, fmt, true);
            if (parsed.isValid()) return parsed.format("YYYY-MM-DD");
        }
        const isoOrDefault = dayjs(stringValue);
        if (isoOrDefault.isValid()) return isoOrDefault.format("YYYY-MM-DD");
        return "";
    };

    const formatPermalinkFromClientName = (value) =>
        String(value || "")
            .toLowerCase()
            .replace(/\s+/g, "");

    const getSelectedOption = (options, value) =>
        options.find((option) => String(option?.value) === String(value)) || null;

    const resolvePlanLabel = (planId, options = [], patchFromApi = {}) => {
        const selectedPlan = (Array.isArray(options) ? options : []).find(
            (option) => String(option?.value) === String(planId)
        );
        const apiPlan = patchFromApi?.plan;
        const apiPlanLabel =
            typeof apiPlan === "string" && String(apiPlan).trim() && String(apiPlan) !== String(planId)
                ? apiPlan
                : "";
        return (
            selectedPlan?.label ||
            patchFromApi?.subscribed ||
            patchFromApi?.package ||
            patchFromApi?.planName ||
            patchFromApi?.planDisplay ||
            apiPlanLabel ||
            String(planId || "").trim()
        );
    };

    const resolveClientRegisterRowId = (row = {}) =>
        String(row?.id ?? row?._id ?? "").trim();

    const resolveClientRegisterDisplayLabel = (row = {}, fallbackId = "") => {
        const label = String(
            row?.client ||
                row?.clientName ||
                row?.name ||
                row?.company ||
                row?.companyName ||
                ""
        ).trim();
        return label || String(fallbackId || "").trim();
    };

    const mapClientRegisterRecordToRow = useCallback((item = {}) => {
        const planLabel =
            item?.subscribed ||
            item?.isSubscribed ||
            item?.package ||
            item?.planName ||
            item?.planDisplay ||
            (typeof item?.plan === "string" &&
            String(item.plan).trim() &&
            String(item.plan) !== String(item?.planId ?? "")
                ? item.plan
                : "");
        return {
            ...item,
            id: item?.id || item?._id || "",
            company: item?.company || item?.companyName || "",
            client: item?.client || item?.clientName || item?.name || "",
            contact: item?.contact || item?.contactName || "",
            email: item?.email || "",
            phone: item?.phone || item?.phoneNumber || "",
            subscribed: planLabel,
            plan: item?.plan || planLabel,
            package: item?.package || planLabel,
            billDate: formatDateToMMDDYYYY(item?.billDate || item?.bill_date || item?.billingDate || ""),
            approved: item?.approved || item?.approvedDate || "",
            regDate: formatDateToMMDDYYYY(item?.regDate || item?.reg_date || item?.createdOn || ""),
            lastActivation: formatDateToMMDDYYYY(item?.lastActivation),
            status: item?.status || "deactive",
        };
    }, []);

    const applyClientRegisterRowPatch = useCallback(
        (rowId, partialUpdate = {}) => {
            const targetId = String(rowId ?? "").trim();
            if (!targetId || !partialUpdate || typeof partialUpdate !== "object") return;
            setClientRegisterData((previousRows) => {
                let rowMatched = false;
                const nextRows = previousRows.map((tableRow) => {
                    if (resolveClientRegisterRowId(tableRow) !== targetId) return tableRow;
                    rowMatched = true;
                    return mapClientRegisterRecordToRow({
                        ...tableRow,
                        ...partialUpdate,
                        id: partialUpdate?.id ?? tableRow?.id ?? targetId,
                    });
                });
                return rowMatched ? nextRows : previousRows;
            });
        },
        [mapClientRegisterRecordToRow]
    );

    const extractClientRegisterPatchFromApiResponse = (response) => {
        const responseData = response?.data;
        return responseData && typeof responseData === "object" ? responseData : {};
    };

  useEffect(() => {
        fetchClientRegisterContextData?.();
        getCommonPlanOptions().then((response) => {
            setPlanOptions(mapCommonPlanApiToSelectOptions(response));
        });
  }, []);

    useEffect(() => {
        const metadataFilters = clientRegisterContextData?.filters;
        if (!Array.isArray(metadataFilters) || !metadataFilters.length) return;
        setFilterForm((previousFilterState) => {
            const mergedFilterState = { ...previousFilterState };
            let mergedNewKeysIntoState = false;
            metadataFilters.forEach((filterDefinitionRow) => {
                const fieldKey = filterDefinitionRow?.field;
                if (!fieldKey) return;
                if (!(fieldKey in mergedFilterState)) {
                    mergedFilterState[fieldKey] =
                        filterDefinitionRow?.type === "date" ? null : "";
                    mergedNewKeysIntoState = true;
                }
                if (isMetadataAsyncSelectFilterDefinition(filterDefinitionRow)) {
                    const asyncLabelFieldKey = getAsyncFilterLabelFieldKey(fieldKey);
                    if (!(asyncLabelFieldKey in mergedFilterState)) {
                        mergedFilterState[asyncLabelFieldKey] = "";
                        mergedNewKeysIntoState = true;
                    }
                }
            });
            return mergedNewKeysIntoState ? mergedFilterState : previousFilterState;
        });
    }, [clientRegisterContextData?.filters]);

  useEffect(() => {
        const defaultColumns = [
            { title: "ID", field: "id" },
            { title: "Company", field: "company" },
            { title: "Client", field: "client" },
            { title: "Contact", field: "contact" },
            { title: "Email", field: "email" },
            { title: "Phone", field: "phone" },
            { title: "Subscribed", field: "subscribed" },
            { title: "Bill Date", field: "billDate" },
            { title: "Approved", field: "approved" },
            { title: "Reg Date", field: "regDate" },
            { title: "Status", field: "status" },
        ];
        const tableColumns = clientRegisterContextData?.table?.columns?.length
            ? clientRegisterContextData?.table?.columns
            : defaultColumns;
        const valueReModified = tableColumns?.map((col) => ({
        Header: col?.title,
            accessor: pascalToCamel(col?.field),
            type: col?.type,
      }));
    setTableMetadata(valueReModified);
    }, [clientRegisterContextData]);

    const buildPayloadFromFilterForm = useCallback(
        (form = {}, overrides = {}) => {
            const metadataFilters = clientRegisterContextData?.filters || [];
            const looselyMapped = {};
            metadataFilters.forEach((def) => {
                const field = def?.field;
                if (!field) return;
                looselyMapped[field] = form?.[field];
            });

            return normalizeClientRegisterFilterPayload(
                {
                    pageNumber: 1,
                    pageSize: requestPayload.pageSize || DEFAULT_PAGE_SIZE,
                    ...looselyMapped,
                    nameContains: strOrNull(
                        form?.nameContains ?? form?.search
                    ),
                    planId: strOrNull(form?.planId),
                    statusId: strOrNull(form?.statusId),
                    isNegative: form?.isNegative,
                    fromDate: form?.fromDate,
                    toDate: form?.toDate,
                    ...overrides,
                },
                metadataFilters
            );
        },
        [clientRegisterContextData?.filters, requestPayload.pageSize]
    );

    const getClientRegisterList = async (payloadOverrides = {}) => {
    setLoading(true);
    const payload = normalizeClientRegisterFilterPayload(
      {
        ...requestPayload,
        ...payloadOverrides,
      },
      clientRegisterContextData?.filters || []
    );
        const response = await submitClientRegisterFilterData(payload);
    const responseData = response?.data || {};
    const listData = responseData?.records || [];
        const valueReModified = (Array.isArray(listData) ? listData : []).map(mapClientRegisterRecordToRow);

    if (response) {
            setClientRegisterData(valueReModified || []);
      setPaginationMeta({
        pageNumber: Number(responseData?.pageNumber) || Number(payload?.pageNumber) || 1,
        pageSize: Number(responseData?.pageSize) || Number(payload?.pageSize) || DEFAULT_PAGE_SIZE,
        totalPages: Number(responseData?.totalPages) || 0,
        totalRecords: Number(responseData?.totalRecords) || 0,
      });
      setRequestPayload(payload);
    } else {
            setClientRegisterData([]);
    }
    setLoading(false);
  };

  /** 1) settings page size ready → 2) call filter API once */
  useEffect(() => {
        if (!settingsReady || initialFilterFetchedRef.current) return;
        initialFilterFetchedRef.current = true;
        const pageSize = Number(settingsPageSize) || DEFAULT_PAGE_SIZE;
        setRequestPayload((previous) => ({ ...previous, pageSize }));
        setPaginationMeta((previous) => ({ ...previous, pageSize }));
        getClientRegisterList({ pageNumber: 1, pageSize });
  }, [settingsReady, settingsPageSize]);

  const handleEdit = (row) => {
        const targetId = row?.id || row?._id || "";
        navigate(`/${urlEndPoint.clientRegisterAddEdit}`, { state: { itemId: targetId } });
    };

    const handleCopy = (row) => {
        const targetId = row?.id || row?._id || "";
        navigate(`/${urlEndPoint.clientRegisterAddEdit}`, {
            state: { itemId: targetId, copyMode: true },
        });
    };

    const handleView = (row) => {
        const targetId = row?.id || row?._id || "";
        navigate(`/${urlEndPoint.clientRegisterAddEdit}`, {
            state: { itemId: targetId, viewMode: true },
        });
    };

    const handleJobDescription = (row) => {
        const targetId = row?.id || row?._id || "";
        navigate(`/${urlEndPoint.ClientJobDescription}/${targetId}`);
    }

    const handleAppreciation = (row) => {
        const targetId = row?.id || row?._id || "";
        navigate(`/${urlEndPoint.clientAppreciations}/${targetId}`);
    }

  // Video action should route to manage-user with company id.
    const handleBill = (row) => {
        setBillItemId(row?.id || row?._id || "");
        setBillForm({
            billingDate: formatDateForInput(
                row?.billingDate || row?.billDate || row?.billing_date || row?.bill_date || ""
            ),
        });
        setBillErrors({});
        setBillModalOpen(true);
    };

    const closeBillModal = () => {
        setBillModalOpen(false);
        setBillItemId("");
        setBillForm({ billingDate: "" });
        setBillErrors({});
        setBillSubmitting(false);
    };

    const handlePermalink = (row) => {
        setPermalinkItemId(row?.id || row?._id || "");
        if (row?.id) {
            getRegisterFetchData(row, "permalink");
        } else {
            setPermalinkForm({
                permalink: formatPermalinkFromClientName(row?.clientName || row?.client || row?.name || ""),
            });
        }
        setPermalinkErrors({});
        setPermalinkModalOpen(true);
    };

    const getRegisterFetchData = async (registerData, formType = "") => {
        const payload = {
            _Id: registerData?.id
        };
        const response = await getClientRegisterFetchData(payload);
        const responseData = response?.data || {};

        if (response) {
            if (formType === "permalink") {
                setPermalinkForm({
                    permalink:
                        responseData?.permalink ||
                        formatPermalinkFromClientName(
                            registerData?.clientName || registerData?.client || registerData?.name || ""
                        ),
                });
            }

            if (formType === "incentive") {
                setIncentiveForm({
                    min: responseData?.targetMin ?? responseData?.min ?? responseData?.incentiveTargetMin ?? "",
                    max: responseData?.targetMax ?? responseData?.max ?? responseData?.incentiveTargetMax ?? "",
                    perAmount: responseData?.perAmount ?? responseData?.incentivePerAmount ?? "",
                    isNegative: Boolean(
                        responseData?.incentiveNegative ?? responseData?.isNegative ?? false
                    ),
                    planId: responseData?.planId || "",
                    maxQuota: responseData?.maxQuota ?? "",
                    quotaStartDate: formatDateForInput(responseData?.quotaStartDate || ""),
                });
            }
        }
    };

    const closePermalinkModal = () => {
        setPermalinkModalOpen(false);
        setPermalinkItemId("");
        setPermalinkForm({ permalink: "" });
        setPermalinkErrors({});
        setPermalinkSubmitting(false);
    };

    const handleOffboarding = async (row) => {
        const targetId = row?.id || row?._id || "";
        setOffboardingItemId(targetId);
        setOffboardingForm({
            lastWorkingDay: "",
            note: "",
        });
        setOffboardingErrors({});
        setOffboardingModalOpen(true);

        if (!targetId) return;

        setOffboardingFetching(true);
        const response = await getOffboardingByClientId(targetId);
        if (response) {
            const responseData = response?.data || {};
            setOffboardingForm({
                lastWorkingDay: formatDateForInput(responseData?.lastWorkingDate || ""),
                note: responseData?.note ?? "",
            });
        }
        setOffboardingFetching(false);
    };
    const handlePreQuestioning = (row) => {
        const targetId = row?.id || row?._id || "";
        navigate(`/${urlEndPoint.preQuestioning}/${targetId}`);
    };

    const closeOffboardingModal = () => {
        setOffboardingModalOpen(false);
        setOffboardingItemId("");
        setOffboardingForm({ lastWorkingDay: "", note: "" });
        setOffboardingErrors({});
        setOffboardingSubmitting(false);
        setOffboardingFetching(false);
    };

    const handleIncentiveTarget = (row) => {
        // console.log('....row', row)
        setIncentiveType(row?.incentive)
        setIncentiveItemId(row?.id || row?._id || "");
        setIncentiveForm({
            min: row?.min ?? row?.incentiveTargetMin ?? "",
            max: row?.max ?? row?.incentiveTargetMax ?? "",
            perAmount: row?.perAmount ?? row?.incentivePerAmount ?? "",
            isNegative: Boolean(row?.isNegative ?? row?.incentiveNegative ?? false),
            planId: row?.planId || "",
            maxQuota: row?.maxQuota ?? "",
            quotaStartDate: formatDateForInput(row?.quotaStartDate || ""),
        });
        if (row?.id) {
            getRegisterFetchData(row, "incentive");
        }
        setIncentiveErrors({});
        setIncentiveModalOpen(true);
    };

    const handleAdvantageLogin = (row) => {
        const targetId = row?.id || row?._id || "";
        setAdvantageLoginItemId(targetId);
        setAdvantageLoginClientName(
            String(
                row?.clientName || row?.client || row?.company || row?.name || ""
            ).trim()
        );
        setAdvantageForm({
            account: "",
            user: "",
            password: "",
            secret: "",
            isOtpBased: false,
            outlookEmail: "",
            userEmail: "",
            isOld: false,
            sameAsFirstAdvantage: false,
        });
        setAdvantageSourceData({
            fadv: {
                account: "",
                user: "",
                password: "",
                secret: "",
                isOtpBased: false,
                outlookEmail: "",
                userEmail: "",
                isOld: false,
            },
            aiFadv: {
                account: "",
                user: "",
                password: "",
                secret: "",
                isOtpBased: false,
                outlookEmail: "",
                userEmail: "",
                isOld: false,
            },
        });
        setAdvantageErrors({});
        setAdvantageLoginModalOpen(true);
        if (!targetId) return;

        setAdvantageFetching(true);
        getClientRegisterFadvDetails(targetId).then((response) => {
            const responseData = response?.data || {};
            const nextFadv = {
                account: responseData?.fadv?.account || "",
                user: responseData?.fadv?.user || "",
                password: responseData?.fadv?.password || "",
                secret: responseData?.fadv?.secret || "",
                isOtpBased: Boolean(responseData?.fadv?.isOtpBased),
                outlookEmail: responseData?.fadv?.outlookEmail || "",
                userEmail: responseData?.fadv?.userEmail || "",
                isOld: Boolean(responseData?.fadv?.isOld),
            };
            const nextAiFadv = {
                account: responseData?.aiFadv?.account || "",
                user: responseData?.aiFadv?.user || "",
                password: responseData?.aiFadv?.password || "",
                secret: responseData?.aiFadv?.secret || "",
                isOtpBased: Boolean(responseData?.aiFadv?.isOtpBased),
                outlookEmail: responseData?.aiFadv?.outlookEmail || "",
                userEmail: responseData?.aiFadv?.userEmail || "",
                isOld: Boolean(responseData?.aiFadv?.isOld),
            };
            const isSameAsFirstAdvantage = Boolean(
                responseData?.sameAsFirstAdvantage || responseData?.isSameAsFirstAdvantage
            );
            if (response) {
                setAdvantageSourceData({
                    fadv: nextFadv,
                    aiFadv: nextAiFadv,
                });
                const selectedData = isSameAsFirstAdvantage ? nextFadv : nextAiFadv;
                setAdvantageForm((prev) => ({
                    ...prev,
                    account: selectedData?.account || "",
                    user: selectedData?.user || "",
                    password: selectedData?.password || "",
                    secret: selectedData?.secret || "",
                    isOtpBased: Boolean(selectedData?.isOtpBased),
                    outlookEmail: selectedData?.outlookEmail || "",
                    userEmail: selectedData?.userEmail || "",
                    isOld: Boolean(
                        selectedData?.isOtpBased && selectedData?.isOld
                    ),
                    sameAsFirstAdvantage: isSameAsFirstAdvantage,
                }));
                return;
            }
            toast.error(response?.message || "Unable to fetch First Advantage details");
        }).finally(() => setAdvantageFetching(false));
    };

    const closeIncentiveModal = () => {
        setIncentiveModalOpen(false);
        setIncentiveItemId("");
        setIncentiveForm({
            min: "",
            max: "",
            perAmount: "",
            isNegative: false,
            planId: "",
            maxQuota: "",
            quotaStartDate: "",
        });
        setIncentiveErrors({});
        setIncentiveSubmitting(false);
    };

    const closeAdvantageLoginModal = () => {
        setAdvantageLoginModalOpen(false);
        setAdvantageLoginItemId("");
        setAdvantageLoginClientName("");
        setAdvantageForm({
            account: "",
            user: "",
            password: "",
            secret: "",
            isOtpBased: false,
            outlookEmail: "",
            userEmail: "",
            isOld: false,
            sameAsFirstAdvantage: false,
        });
        setAdvantageSourceData({
            fadv: {
                account: "",
                user: "",
                password: "",
                secret: "",
                isOtpBased: false,
                outlookEmail: "",
                userEmail: "",
                isOld: false,
            },
            aiFadv: {
                account: "",
                user: "",
                password: "",
                secret: "",
                isOtpBased: false,
                outlookEmail: "",
                userEmail: "",
                isOld: false,
            },
        });
        setAdvantageErrors({});
        setAdvantageSubmitting(false);
        setAdvantageFetching(false);
    };

    const hasAdvantageCredentialData = (data = {}) =>
        Boolean(
            String(data?.account || "").trim() ||
                String(data?.user || "").trim() ||
                String(data?.password || "").trim() ||
                String(data?.secret || "").trim()
        );

    const handleAdvantageCheckboxChange = (checked) => {
        if (checked) {
            const selectedData = advantageSourceData?.fadv;
            setAdvantageForm((prev) => ({
                ...prev,
                account: selectedData?.account || "",
                user: selectedData?.user || "",
                password: selectedData?.password || "",
                secret: selectedData?.secret || "",
                isOtpBased: Boolean(selectedData?.isOtpBased),
                outlookEmail: selectedData?.outlookEmail || "",
                userEmail: selectedData?.userEmail || "",
                isOld: Boolean(selectedData?.isOtpBased && selectedData?.isOld),
                sameAsFirstAdvantage: true,
            }));
            return;
        }

        const aiFadvData = advantageSourceData?.aiFadv;
        if (hasAdvantageCredentialData(aiFadvData)) {
            setAdvantageForm((prev) => ({
                ...prev,
                account: aiFadvData?.account || "",
                user: aiFadvData?.user || "",
                password: aiFadvData?.password || "",
                secret: aiFadvData?.secret || "",
                isOtpBased: Boolean(aiFadvData?.isOtpBased),
                outlookEmail: aiFadvData?.outlookEmail || "",
                userEmail: aiFadvData?.userEmail || "",
                isOld: Boolean(aiFadvData?.isOtpBased && aiFadvData?.isOld),
                sameAsFirstAdvantage: false,
            }));
            return;
        }

        setAdvantageForm((prev) => ({
            ...prev,
            sameAsFirstAdvantage: false,
        }));
  };

    const handleFilterSubmit = async (modalFormPayload = {}) => {
        const mergedFilterForm = mergeModalSubmitIntoListFilter(
            filterForm,
            clientRegisterFilterModalData,
            modalFormPayload
        );
        setFilterForm(mergedFilterForm);
        setCommonFilterModalOpen(false);
        await getClientRegisterList(buildPayloadFromFilterForm(mergedFilterForm));
    };

    const handleFilterReset = useCallback(async () => {
        const emptyFilter = buildBaselineEmptyListFilter();
        setFilterForm(emptyFilter);
        setCommonFilterModalOpen(false);
        await getClientRegisterList({
            ...buildPayloadFromFilterForm(emptyFilter),
            pageNumber: 1,
            pageSize: paginationMeta.pageSize || DEFAULT_PAGE_SIZE,
        });
    }, [buildBaselineEmptyListFilter, buildPayloadFromFilterForm, paginationMeta.pageSize]);

    useEffect(() => {
        if (!commonResetFilter) return;
        const run = async () => {
            await handleFilterReset();
            setCommonResetFilter(false);
        };
        run();
    }, [commonResetFilter, handleFilterReset]);

    const handleToolbarTextFilterInputChange = (fieldKey, nextValue) => {
        setFilterForm((previousFilterState) => ({
            ...previousFilterState,
            [fieldKey]: nextValue ?? "",
        }));
    };

    const handleToolbarReactSelectFilterChange = (fieldKey, selectedOption) => {
        setFilterForm((previousFilterState) => ({
            ...previousFilterState,
            [fieldKey]: selectedOption?.value ?? "",
            [getAsyncFilterLabelFieldKey(fieldKey)]: selectedOption?.label ?? "",
        }));
    };

    const handleUpperSearch = () => {
        getClientRegisterList(buildPayloadFromFilterForm(filterForm));
    };

  const handlePageChange = async (page) => {
        await getClientRegisterList({ pageNumber: page });
  };

  const handleRowsPerPageChange = async (rows) => {
        await getClientRegisterList({ pageNumber: 1, pageSize: rows });
    };

    const aiRecruiterSelectedRowIds = useMemo(
        () => Object.keys(aiRecruiterSelectedClientsById),
        [aiRecruiterSelectedClientsById]
    );

    const handleAiRecruiterSelectedRowIdsChange = useCallback(
        (nextIds = []) => {
            const normalizedIds = (Array.isArray(nextIds) ? nextIds : [])
                .map((id) => String(id ?? "").trim())
                .filter(Boolean);

            setAiRecruiterSelectedClientsById((previous) => {
                const next = {};
                normalizedIds.forEach((id) => {
                    if (previous[id]) {
                        next[id] = previous[id];
                        return;
                    }
                    const row = clientRegisterData.find(
                        (item) => resolveClientRegisterRowId(item) === id
                    );
                    next[id] = {
                        id,
                        label: row
                            ? resolveClientRegisterDisplayLabel(row, id)
                            : id,
                    };
                });
                return next;
            });
        },
        [clientRegisterData]
    );

    const aiRecruiterSelectedClientsList = useMemo(
        () =>
            Object.values(aiRecruiterSelectedClientsById).filter((client) =>
                String(client?.id ?? "").trim()
            ),
        [aiRecruiterSelectedClientsById]
    );

    const openSelectionReviewModal = useCallback(() => {
        if (!aiRecruiterSelectedClientsList.length) return;
        setSelectionReviewModalOpen(true);
    }, [aiRecruiterSelectedClientsList]);

    const closeSelectionReviewModal = useCallback(() => {
        setSelectionReviewModalOpen(false);
    }, []);

    const handleSelectionReviewClientDeselect = useCallback((clientId) => {
        const id = String(clientId ?? "").trim();
        if (!id) return;
        setAiRecruiterSelectedClientsById((previous) => {
            if (!previous[id]) return previous;
            const next = { ...previous };
            delete next[id];
            return next;
        });
        if (aiRecruiterSelectedRowIds.length <= 1) {
            setSelectionReviewModalOpen(false);
        }
    }, [aiRecruiterSelectedRowIds.length]);

    const clearAiRecruiterSelection = useCallback(() => {
        setAiRecruiterSelectedClientsById({});
        setSelectionReviewModalOpen(false);
    }, []);

    const handleConfirmMoveToAiRecruiter = useCallback(
        async (ids = []) => {
            const normalizedIds = (Array.isArray(ids) ? ids : [])
                .map((id) => String(id ?? "").trim())
                .filter(Boolean);
            if (!normalizedIds.length) {
                toast.error("Select at least one client to move");
                return false;
            }

            const response = await moveClientRegisterToAiRecruiterMultiple({
                ids: normalizedIds,
            });
            if (response?.success || response?.isSuccess || response?.status) {
                toast.success(response?.message || "Clients moved to AI recruiter successfully");
                clearAiRecruiterSelection();
                await getClientRegisterList({ pageNumber: paginationMeta?.pageNumber || 1 });
                return true;
            }
            toast.error(
                response?.message ||
                    response?.error?.message ||
                    "Unable to move clients to AI recruiter"
            );
            return false;
        },
        [clearAiRecruiterSelection, paginationMeta?.pageNumber]
    );

    const handleMoveToAiRecruiterBulkClick = useCallback(async () => {
        const ids = aiRecruiterSelectedRowIds;
        if (!ids.length) {
            toast.error("Select at least one client to move");
            return false;
        }
        const result = await Swal.fire({
            title: "Move to AI Recruiter?",
            text: "Are you sure you want to move the selected clients to AI recruiter?",
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Yes, move",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });
        if (!result.isConfirmed) return false;
        return await handleConfirmMoveToAiRecruiter(ids);
    }, [aiRecruiterSelectedRowIds, handleConfirmMoveToAiRecruiter]);

    const handleConfirmDisableAiRecruiter = useCallback(
        async (ids = []) => {
            const normalizedIds = (Array.isArray(ids) ? ids : [])
                .map((id) => String(id ?? "").trim())
                .filter(Boolean);
            if (!normalizedIds.length) {
                toast.error("Select at least one client");
                return false;
            }

            const response = await disableClientRegisterAiRecruiterMultiple({
                ids: normalizedIds,
            });
            if (response?.success || response?.isSuccess || response?.status) {
                toast.success(
                    response?.message || "AI recruiter disabled for selected clients successfully"
                );
                clearAiRecruiterSelection();
                await getClientRegisterList({ pageNumber: paginationMeta?.pageNumber || 1 });
                return true;
            }
            toast.error(
                response?.message ||
                    response?.error?.message ||
                    "Unable to disable AI recruiter for selected clients"
            );
            return false;
        },
        [clearAiRecruiterSelection, paginationMeta?.pageNumber]
    );

    const handleDisableAiRecruiterBulkClick = useCallback(async () => {
        const ids = aiRecruiterSelectedRowIds;
        if (!ids.length) {
            toast.error("Select at least one client");
            return false;
        }
        const result = await Swal.fire({
            title: "Remove from AI recruiter?",
            text: "Are you sure you want to remove from AI recruiter for the selected clients?",
            icon: "warning",
            iconColor: "#dc3545",
            showCancelButton: true,
            confirmButtonText: "OK",
            cancelButtonText: "Cancel",
            reverseButtons: true,
        });
        if (!result.isConfirmed) return false;
        return await handleConfirmDisableAiRecruiter(ids);
    }, [aiRecruiterSelectedRowIds, handleConfirmDisableAiRecruiter]);

    const handleClientRegisterAiRecruiterBulkActionSelect = useCallback(
        async (actionKey) => {
            if (actionKey === "move-to-ai-recruiter") {
                return handleMoveToAiRecruiterBulkClick();
            }
            if (actionKey === "remove-from-ai-recruiter") {
                return handleDisableAiRecruiterBulkClick();
            }
            return false;
        },
        [handleMoveToAiRecruiterBulkClick, handleDisableAiRecruiterBulkClick]
    );

    /* -------------------- Submit -------------------- */
    const handleBillSubmit = async (e) => {
        e.preventDefault();

        const nextErrors = {};
        if (!billForm?.billingDate) {
            nextErrors.billingDate = "Billing date is required";
        }

        if (!billItemId) {
            nextErrors.billingDate = "Unable to identify the selected row";
        }

        if (Object.keys(nextErrors).length) {
            setBillErrors(nextErrors);
            return;
        }

        setBillSubmitting(true);
        const payload = {
            billingDate: billForm?.billingDate ? new Date(billForm.billingDate).toISOString() : "",
        };
        const response = await submitClientRegisterBill(billItemId, payload);
        setBillSubmitting(false);

        if (response?.success) {
            toast.success(response?.message || "Billing date saved successfully");
            const patchFromApi = extractClientRegisterPatchFromApiResponse(response);
            applyClientRegisterRowPatch(billItemId, {
                id: billItemId,
                billingDate: payload.billingDate,
                billDate: payload.billingDate,
                bill_date: payload.billingDate,
                ...patchFromApi,
            });
            closeBillModal();
        } else {
            toast.error(response?.message || "Unable to save billing date");
        }
    };

    const handlePermalinkSubmit = async (e) => {
        e.preventDefault();

        const nextErrors = {};
        if (!String(permalinkForm?.permalink || "").trim()) {
            nextErrors.permalink = "Permalink is required";
        }

        if (!permalinkItemId) {
            nextErrors.permalink = "Unable to identify the selected row";
        }

        if (Object.keys(nextErrors).length) {
            setPermalinkErrors(nextErrors);
            return;
        }

        setPermalinkSubmitting(true);
        const payload = {
            permalink: String(permalinkForm?.permalink || "").trim(),
        };
        const response = await submitClientRegisterPermalink(permalinkItemId, payload);
        setPermalinkSubmitting(false);

        if (response?.success) {
            toast.success(response?.message || "Permalink saved successfully");
            const patchFromApi = extractClientRegisterPatchFromApiResponse(response);
            applyClientRegisterRowPatch(permalinkItemId, {
                id: permalinkItemId,
                permalink: payload.permalink,
                ...patchFromApi,
            });
            closePermalinkModal();
        } else {
            toast.error(response?.message || "Unable to save permalink");
        }
    };

    const handleOffboardingSubmit = async (e) => {
        e.preventDefault();

        const nextErrors = {};
        if (!offboardingForm?.lastWorkingDay) {
            nextErrors.lastWorkingDay = "Last working day is required";
        }
        if (!String(offboardingForm?.note || "").trim()) {
            nextErrors.note = "Note is required";
        }
        if (!offboardingItemId) {
            nextErrors.lastWorkingDay = "Unable to identify the selected row";
        }

        if (Object.keys(nextErrors).length) {
            setOffboardingErrors(nextErrors);
            return;
        }

        setOffboardingSubmitting(true);
        const payload = {
            clientId: offboardingItemId,
            lastWorkingDay: offboardingForm?.lastWorkingDay
                ? new Date(offboardingForm.lastWorkingDay).toISOString()
                : "",
            note: String(offboardingForm?.note || "").trim(),
        };
        const response = await addOffboarding(payload);
        setOffboardingSubmitting(false);

        if (response?.success) {
            toast.success(response?.message || "Offboarding saved successfully");
            const patchFromApi = extractClientRegisterPatchFromApiResponse(response);
            applyClientRegisterRowPatch(offboardingItemId, {
                id: offboardingItemId,
                clientId: offboardingItemId,
                lastWorkingDay: payload.lastWorkingDay,
                note: payload.note,
                status: patchFromApi?.status ?? "deactive",
                ...patchFromApi,
            });
            closeOffboardingModal();
        } else {
            toast.error(response?.message || "Unable to save offboarding");
        }
    };

    const handleIncentiveSubmit = async (e) => {
        e.preventDefault();

        const nextErrors = {};
        if (!incentiveItemId) {
            nextErrors.planId = "Unable to identify the selected row";
        }
        if (!String(incentiveForm?.planId || "").trim()) {
            nextErrors.planId = "Plan is required";
        }

        if (Object.keys(nextErrors).length) {
            setIncentiveErrors(nextErrors);
            return;
        }

        setIncentiveSubmitting(true);
        const payload = {
            min: Number(incentiveForm?.min || 0),
            max: Number(incentiveForm?.max || 0),
            perAmount: Number(incentiveForm?.perAmount || 0),
            isNegative: Boolean(incentiveForm?.isNegative),
            planId: String(incentiveForm?.planId || ""),
            maxQuota: Number(incentiveForm?.maxQuota || 0),
            quotaStartDate: incentiveForm?.quotaStartDate
                ? new Date(incentiveForm.quotaStartDate).toISOString()
                : null,
        };
        const response = await submitClientRegisterIncentiveTarget(incentiveItemId, payload);
        setIncentiveSubmitting(false);

        if (response?.success) {
            toast.success(response?.message || "Incentive target saved successfully");
            const patchFromApi = extractClientRegisterPatchFromApiResponse(response);
            const planLabel = resolvePlanLabel(payload.planId, planOptions, patchFromApi);
            applyClientRegisterRowPatch(incentiveItemId, {
                ...patchFromApi,
                id: incentiveItemId,
                min: payload.min,
                max: payload.max,
                targetMin: payload.min,
                targetMax: payload.max,
                perAmount: payload.perAmount,
                incentivePerAmount: payload.perAmount,
                isNegative: payload.isNegative,
                incentiveNegative: payload.isNegative,
                planId: payload.planId,
                plan: planLabel,
                subscribed: planLabel,
                package: planLabel,
                maxQuota: payload.maxQuota,
                quotaStartDate: payload.quotaStartDate,
            });
            closeIncentiveModal();
        } else {
            toast.error(response?.message || "Unable to save incentive target");
        }
    };

    const handleAdvantageSubmit = async (e) => {
        e.preventDefault();

        const nextErrors = validateAiFadvLoginForm(advantageForm, advantageLoginItemId);

        if (Object.keys(nextErrors).length) {
            setAdvantageErrors(nextErrors);
            return;
        }

        setAdvantageSubmitting(true);
        const payload = {
            account: String(advantageForm?.account || "").trim(),
            user: String(advantageForm?.user || "").trim(),
            password: String(advantageForm?.password || "").trim(),
            secret: String(advantageForm?.secret || "").trim(),
            sameAsFirstAdvantage: Boolean(advantageForm?.sameAsFirstAdvantage),
            isOtpBased: Boolean(advantageForm?.isOtpBased),
            outlookEmail: String(advantageForm?.outlookEmail || "").trim(),
            userEmail: String(advantageForm?.userEmail || "").trim(),
            isOld: resolveFadvIsOldForSubmit({
                isOtpBased: advantageForm?.isOtpBased,
                isOld: advantageForm?.isOld,
            }),
        };
        const response = await updateClientRegisterAiFadvDetails(advantageLoginItemId, payload);
        setAdvantageSubmitting(false);

        if (response?.success) {
            toast.success(response?.message || "AI First Advantage details saved successfully");
            const patchFromApi = extractClientRegisterPatchFromApiResponse(response);
            if (Object.keys(patchFromApi).length) {
                applyClientRegisterRowPatch(advantageLoginItemId, {
                    id: advantageLoginItemId,
                    ...patchFromApi,
                });
            }
            closeAdvantageLoginModal();
        } else {
            toast.error(response?.message || "Unable to save AI First Advantage details");
        }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <EcommerceOrdersModal isOpen={modal1} toggle={toggleViewModal} />

          <Card>
            <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="fw-bold mb-3">Client Register</h5>
                                {registerActionPermissions.canCreate ? (
                                    <Row className="mb-3">
                                        <Col xs="12" className="text-md-end">
                                            <Button
                                                color="primary"
                                                onClick={() =>
                                                    navigate(`/${urlEndPoint.clientRegisterAddEdit}`)
                                                }
                                            >
                                                + Create
                                            </Button>
                                        </Col>
                                    </Row>
                                ) : null}
                            </div>
              <hr />

              {shouldRenderMetadataToolbarFilterRow ? (
                <MetadataFilterToolbar
                  toolbarDefinitions={metadataToolbarFilterDefinitions}
                  listFilter={filterForm}
                  onTextChange={handleToolbarTextFilterInputChange}
                  onSelectChange={handleToolbarReactSelectFilterChange}
                  onSearch={handleUpperSearch}
                  onClear={handleFilterReset}
                  showClearWhenNoModalFilters={false}
                  toolbarDropdownOptionsByFieldName={
                    toolbarDropdownReactSelectOptionsByFieldName
                  }
                  className="metadata-filter-toolbar upper-filter-bar manage-user-filter-strip client-register-filter-bar"
                  pageName={CLIENT_REGISTER_PAGE_NAME}
                />
              ) : null}

                            {!loading && tableMetaData?.length ? (
                <TableContainer
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyRegisterRowAction}
                  onEdit={
                      registerActionPermissions.canEdit ? handleEdit : undefined
                  }
                  onCopy={
                      registerActionPermissions.canEdit ? handleCopy : undefined
                  }
                  onView={
                      registerActionPermissions.canView ? handleView : undefined
                  }
                  onBill={registerActionPermissions.canBill ? handleBill : undefined}
                  onJobDescription={
                      registerActionPermissions.canJobDescription
                          ? handleJobDescription
                          : undefined
                  }
                  onAppreciation={
                      registerActionPermissions.canAppreciation
                          ? handleAppreciation
                          : undefined
                  }
                  onPermalink={
                      registerActionPermissions.canPermalink ? handlePermalink : undefined
                  }
                  onOffboarding={
                      registerActionPermissions.canOffboarding
                          ? handleOffboarding
                          : undefined
                  }
                  onPreQuestioning={
                      registerActionPermissions.canPreQuestioning
                          ? handlePreQuestioning
                          : undefined
                  }
                  onIncentiveTarget={
                      registerActionPermissions.canIncentiveTarget
                          ? handleIncentiveTarget
                          : undefined
                  }
                  onAdvantageLogin={
                      registerActionPermissions.canAdvantageLogin
                          ? handleAdvantageLogin
                          : undefined
                  }
                  isCheckboxLeftSide={canMoveToAiRecruiter}
                  enableRowSelection={canMoveToAiRecruiter}
                  toggleSelectionOnRowClick={canMoveToAiRecruiter}
                  bulkSelectionEntitySingular="client"
                  bulkSelectionEntityPlural="clients"
                  onBulkSelectionReviewClick={
                      canMoveToAiRecruiter ? openSelectionReviewModal : undefined
                  }
                  bulkActionSelectOptions={null}
                  bulkActionSelectPlaceholder={null}
                  onBulkActionSelect={null}
                  selectedRowIds={
                      canMoveToAiRecruiter ? aiRecruiterSelectedRowIds : undefined
                  }
                  onSelectedRowIdsChange={
                      canMoveToAiRecruiter
                          ? handleAiRecruiterSelectedRowIdsChange
                          : undefined
                  }
                  resolveRowId={resolveClientRegisterRowId}
                  isPagination={true}
                  currentPage={paginationMeta?.pageNumber}
                  rowsPerPage={paginationMeta?.pageSize}
                  totalPages={paginationMeta?.totalPages}
                  totalRecords={paginationMeta?.totalRecords}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handleRowsPerPageChange}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  setCommonResetFilter={setCommonResetFilter}
                  allCommonFilter={
                    shouldShowTableHeaderFilterAndResetControls &&
                    clientRegisterFilterModalData.length > 0
                  }
                  activeFilterCount={activeModalFilterCount}
                  pageName={CLIENT_REGISTER_PAGE_NAME}
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>

          <BulkSelectionReviewModal
            isOpen={selectionReviewModalOpen}
            onClose={closeSelectionReviewModal}
            title="Selected clients"
            listLabel="Selected clients"
            items={aiRecruiterSelectedClientsList}
            onItemDeselect={handleSelectionReviewClientDeselect}
            onDeselectAll={clearAiRecruiterSelection}
          />

          {clientRegisterFilterModalData.length > 0 ? (
            <AllCommonFilterModal
              setCommonFilterModalOpen={setCommonFilterModalOpen}
              commonFilterModalOpen={commonFilterModalOpen}
              modalTitle="Client Register"
              handleSubmit={handleFilterSubmit}
              filterData={clientRegisterFilterModalData}
              metadataFilterDefinitions={metadataFiltersAll}
              enableFilterToolbarCustomization={usesMetadataDrivenFilterUi}
              commonResetFilter={commonResetFilter}
              setCommonResetFilter={setCommonResetFilter}
              pageName={CLIENT_REGISTER_PAGE_NAME}
            />
          ) : null}

                    <Modal
                        isOpen={billModalOpen}
                        toggle={closeBillModal}
                        backdrop="static"
                        size="md"
                        centered
                        className="modal-full-height"
                    >
                        <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
                            <Form onSubmit={handleBillSubmit}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="fw-bold mb-0">Bill</h5>
                                    <Button close onClick={closeBillModal} />
                                </div>

                                <hr />
                                <Row className="justify-content-between align-items-center">
                                    <Col md={12} className="mt-2">
                                        <Label>
                                            Billing Date <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="date"
                                            name="billingDate"
                                            value={billForm.billingDate || ""}
                                            onChange={(e) => {
                                                const nextValue = e?.target?.value || "";
                                                setBillForm((prev) => ({ ...prev, billingDate: nextValue }));
                                                if (billErrors?.billingDate) {
                                                    setBillErrors((prev) => ({ ...prev, billingDate: "" }));
                                                }
                                            }}
                                        />
                                        {billErrors?.billingDate && (
                                            <small className="text-danger">{billErrors.billingDate}</small>
                                        )}
                                    </Col>
                                </Row>

                                <div className="text-end mt-3">
                                    <Button color="primary" type="submit" disabled={billSubmitting}>
                                        {billSubmitting ? "Saving..." : "Save"}
                                    </Button>
                                </div>
                            </Form>
                        </ModalBody>
                    </Modal>

                    <Modal
                        isOpen={permalinkModalOpen}
                        toggle={closePermalinkModal}
                        backdrop="static"
                        size="md"
                        centered
                        className="modal-full-height"
                    >
                        <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
                            <Form onSubmit={handlePermalinkSubmit}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="fw-bold mb-0">Permalink</h5>
                                    <Button close onClick={closePermalinkModal} />
                                </div>

                                <hr />
                                <Row className="justify-content-between align-items-center">
                                    <Col md={12} className="mt-2">
                                        <Label>
                                            Permalink <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="text"
                                            name="permalink"
                                            value={permalinkForm.permalink || ""}
                                            onChange={(e) => {
                                                const nextValue = e?.target?.value || "";
                                                setPermalinkForm((prev) => ({ ...prev, permalink: nextValue }));
                                                if (permalinkErrors?.permalink) {
                                                    setPermalinkErrors((prev) => ({ ...prev, permalink: "" }));
                                                }
                                            }}
                                            placeholder="Enter permalink"
                                        />
                                        {permalinkErrors?.permalink && (
                                            <small className="text-danger">{permalinkErrors.permalink}</small>
                                        )}
                                    </Col>
                                </Row>

                                <div className="text-end mt-3">
                                    <Button color="primary" type="submit" disabled={permalinkSubmitting}>
                                        {permalinkSubmitting ? "Saving..." : "Save"}
                                    </Button>
                                </div>
                            </Form>
                        </ModalBody>
                    </Modal>

                    <Modal
                        isOpen={offboardingModalOpen}
                        toggle={closeOffboardingModal}
                        backdrop="static"
                        size="md"
                        centered
                        className="modal-full-height"
                    >
                        <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
                            <Form onSubmit={handleOffboardingSubmit}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="fw-bold mb-0">Offboarding</h5>
                                    <Button close onClick={closeOffboardingModal} />
                                </div>

                                <hr />
                                <Row className="justify-content-between align-items-center">
                                    <Col md={12} className="mt-2">
                                        <Label>
                                            Last Working Day <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="date"
                                            name="lastWorkingDay"
                                            value={offboardingForm.lastWorkingDay || ""}
                                            disabled={offboardingFetching}
                                            onChange={(e) => {
                                                const nextValue = e?.target?.value || "";
                                                setOffboardingForm((prev) => ({ ...prev, lastWorkingDay: nextValue }));
                                                if (offboardingErrors?.lastWorkingDay) {
                                                    setOffboardingErrors((prev) => ({ ...prev, lastWorkingDay: "" }));
                                                }
                                            }}
                                        />
                                        {offboardingErrors?.lastWorkingDay && (
                                            <small className="text-danger">{offboardingErrors.lastWorkingDay}</small>
                                        )}
                                    </Col>
                                    <Col md={12} className="mt-3">
                                        <Label>
                                            Notes <span className="text-danger">*</span>
                                        </Label>
                                        <Input
                                            type="textarea"
                                            name="note"
                                            value={offboardingForm.note || ""}
                                            disabled={offboardingFetching}
                                            onChange={(e) => {
                                                const nextValue = e?.target?.value || "";
                                                setOffboardingForm((prev) => ({ ...prev, note: nextValue }));
                                                if (offboardingErrors?.note) {
                                                    setOffboardingErrors((prev) => ({ ...prev, note: "" }));
                                                }
                                            }}
                                            placeholder="Enter notes"
                                        />
                                        {offboardingErrors?.note && (
                                            <small className="text-danger">{offboardingErrors.note}</small>
                                        )}
                                    </Col>
                                </Row>

                                <div className="text-end mt-3">
                                    <Button
                                        color="primary"
                                        type="submit"
                                        disabled={offboardingSubmitting || offboardingFetching}
                                    >
                                        {offboardingSubmitting ? "Saving..." : "Save"}
                                    </Button>
                                </div>
                            </Form>
                        </ModalBody>
                    </Modal>

                    <Modal
                        isOpen={incentiveModalOpen}
                        toggle={closeIncentiveModal}
                        backdrop="static"
                        size="lg"
                        centered
                        className="modal-full-height"
                    >
                        <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
                            <Form onSubmit={handleIncentiveSubmit}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h5 className="fw-bold mb-0">INCENTIVE/QUOTA TARGET</h5>
                                    <Button close onClick={closeIncentiveModal} />
                                </div>

                                <hr />

                                <h4 className="mb-4" style={{ color: "#38a4f8" }}>Incentive Settings</h4>
                                <Row className="align-items-center mb-3">
                                    <Col md={3}>
                                        <Label className="mb-0">Incentive Type</Label>
                                    </Col>
                                    <Col md={9}>
                                        <span className={`px-2 py-1 fw-semibold ${incentiveType(incentiveTypes)} ${incentiveTypes === 'monthly' ? 'incentive-template' : ''}`}>
                                            {incentiveTypes || ""}
                                        </span>
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={3}>
                                        <Label className="mt-2">
                                            Plan <span className="text-danger">*</span>
                                        </Label>
                                    </Col>
                                    <Col md={9}>
                                        <Select
                                            options={planOptions}
                                            className="select2-selection"
                                            placeholder="Select Plan"
                                            value={getSelectedOption(planOptions, incentiveForm.planId)}
                                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                                            menuPosition="fixed"
                                            styles={{
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                menu: (base) => ({ ...base, zIndex: 9999 }),
                                            }}
                                            onChange={(selectedOption) => {
                                                const selectedValue = selectedOption?.value || "";
                                                setIncentiveForm((prev) => ({
                                                    ...prev,
                                                    planId: selectedValue,
                                                    min:
                                                        selectedOption?.incentiveMin != null
                                                            ? String(selectedOption.incentiveMin)
                                                            : prev.min,
                                                    max:
                                                        selectedOption?.incentiveMax != null
                                                            ? String(selectedOption.incentiveMax)
                                                            : prev.max,
                                                }));
                                                if (incentiveErrors?.planId) {
                                                    setIncentiveErrors((prev) => ({ ...prev, planId: "" }));
                                                }
                                            }}
                                        />
                                        {incentiveErrors?.planId && (
                                            <small className="text-danger">{incentiveErrors.planId}</small>
                                        )}
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={3}>
                                        <Label className="mt-2">Min Target</Label>
                                    </Col>
                                    <Col md={3}>
                                        <Input
                                            type="number"
                                            value={incentiveForm.min}
                                            onChange={(e) =>
                                                setIncentiveForm((prev) => ({ ...prev, min: e?.target?.value || "" }))
                                            }
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Label className="mt-2">Max Target</Label>
                                    </Col>
                                    <Col md={3}>
                                        <Input
                                            type="number"
                                            value={incentiveForm.max}
                                            onChange={(e) =>
                                                setIncentiveForm((prev) => ({ ...prev, max: e?.target?.value || "" }))
                                            }
                                        />
                                    </Col>
                                </Row>

                                <Row className="mb-4">
                                    <Col md={3}>
                                        <Label className="mt-2">Per Ammount</Label>
                                    </Col>
                                    <Col md={3}>
                                        <Input
                                            type="number"
                                            value={incentiveForm.perAmount}
                                            onChange={(e) =>
                                                setIncentiveForm((prev) => ({ ...prev, perAmount: e?.target?.value || "" }))
                                            }
                                        />
                                    </Col>
                                    <Col md={4}>
                                        <Label className="mt-2">
                                            Has Negative Incentive ? <span className="text-danger">*</span>
                                        </Label>
                                    </Col>
                                    <Col md={2} className="d-flex align-items-center">
                                        <Input
                                            key={`incentive-negative-${incentiveItemId}-${Number(!!incentiveForm.isNegative)}`}
                                            type="checkbox"
                                            defaultChecked={!!incentiveForm.isNegative}
                                            onChange={(e) =>
                                                setIncentiveForm((prev) => ({ ...prev, isNegative: e?.target?.checked }))
                                            }
                                        />
                                    </Col>
                                </Row>

                                <h4 className="mb-4" style={{ color: "#38a4f8" }}>Quota Settings</h4>

                                <Row className="mb-3">
                                    <Col md={3}>
                                        <Label className="mt-2">Start Date</Label>
                                    </Col>
                                    <Col md={6}>
                                        <Input
                                            type="date"
                                            value={incentiveForm.quotaStartDate}
                                            onChange={(e) => {
                                                const nextValue = e?.target?.value || "";
                                                setIncentiveForm((prev) => ({ ...prev, quotaStartDate: nextValue }));
                                                if (incentiveErrors?.quotaStartDate) {
                                                    setIncentiveErrors((prev) => ({ ...prev, quotaStartDate: "" }));
                                                }
                                            }}
                                        />
                                        {incentiveErrors?.quotaStartDate && (
                                            <small className="text-danger">{incentiveErrors.quotaStartDate}</small>
                                        )}
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={3}>
                                        <Label className="mt-2">Max Quota</Label>
                                    </Col>
                                    <Col md={6}>
                                        <Input
                                            type="number"
                                            value={incentiveForm.maxQuota}
                                            onChange={(e) =>
                                                setIncentiveForm((prev) => ({ ...prev, maxQuota: e?.target?.value || "" }))
                                            }
                                        />
                                    </Col>
                                </Row>

                                <div className="text-end mt-4">
                                    <Button color="primary" type="submit" disabled={incentiveSubmitting}>
                                        {incentiveSubmitting ? "Saving..." : "Update"}
                                    </Button>
                                </div>
                            </Form>
                        </ModalBody>
                    </Modal>

                    <Modal
                        isOpen={advantageLoginModalOpen}
                        toggle={closeAdvantageLoginModal}
                        backdrop="static"
                        size="lg"
                        centered
                        className="modal-full-height"
                    >
                        <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
                            <Form onSubmit={handleAdvantageSubmit}>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <h5 className="fw-bold mb-0">AI FIRST ADVANTAGE LOGIN</h5>
                                        {advantageLoginClientName ? (
                                            <div className="text-muted mt-1">
                                                {advantageLoginClientName}
                                            </div>
                                        ) : null}
                                    </div>
                                    <Button close onClick={closeAdvantageLoginModal} />
                                </div>

                                <hr />
                                <Row>
                                    <Col md={12}>
                                        <div className="form-check mb-3">
                                            <Input
                                                id="sameAsFirstAdvantage"
                                                className="form-check-input"
                                                type="checkbox"
                                                key={`same-as-first-advantage-${advantageLoginItemId}-${Number(
                                                    !!advantageForm.sameAsFirstAdvantage
                                                )}`}
                                                defaultChecked={!!advantageForm.sameAsFirstAdvantage}
                                                onChange={(e) =>
                                                    handleAdvantageCheckboxChange(!!e?.target?.checked)
                                                }
                                            />
                                            <Label
                                                className="form-check-label"
                                                for="sameAsFirstAdvantage"
                                            >
                                                Same As First Advantage
                                            </Label>
                                        </div>
                                    </Col>
                                </Row>

                                <h4 className="mb-4">AI First Advantage Login Details</h4>

                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Label>Account number <span className="text-danger">*</span></Label>
                                        <Input
                                            type="text"
                                            value={advantageForm.account}
                                            onChange={(e) =>
                                                setAdvantageForm((prev) => ({
                                                    ...prev,
                                                    account: e?.target?.value || "",
                                                }))
                                            }
                                            placeholder="Enter account number"
                                            disabled={advantageFetching}
                                        />
                                        {advantageErrors?.account ? (
                                            <small className="text-danger d-block">
                                                {advantageErrors.account}
                                            </small>
                                        ) : null}
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Label>Login ID <span className="text-danger">*</span></Label>
                                        <Input
                                            type="text"
                                            value={advantageForm.user}
                                            onChange={(e) =>
                                                setAdvantageForm((prev) => ({
                                                    ...prev,
                                                    user: e?.target?.value || "",
                                                }))
                                            }
                                            placeholder="Enter login ID"
                                            disabled={advantageFetching}
                                        />
                                        {advantageErrors?.user ? (
                                            <small className="text-danger d-block">{advantageErrors.user}</small>
                                        ) : null}
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Label>Password <span className="text-danger">*</span></Label>
                                        <Input
                                            type="text"
                                            value={advantageForm.password}
                                            onChange={(e) =>
                                                setAdvantageForm((prev) => ({
                                                    ...prev,
                                                    password: e?.target?.value || "",
                                                }))
                                            }
                                            placeholder="Enter password"
                                            disabled={advantageFetching}
                                        />
                                        {advantageErrors?.password ? (
                                            <small className="text-danger d-block">
                                                {advantageErrors.password}
                                            </small>
                                        ) : null}
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Label>Secret <span className="text-danger">*</span></Label>
                                        <Input
                                            type="text"
                                            value={advantageForm.secret}
                                            onChange={(e) =>
                                                setAdvantageForm((prev) => ({
                                                    ...prev,
                                                    secret: e?.target?.value || "",
                                                }))
                                            }
                                            placeholder="Enter secret"
                                            disabled={advantageFetching}
                                        />
                                        {advantageErrors?.secret ? (
                                            <small className="text-danger d-block">{advantageErrors.secret}</small>
                                        ) : null}
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Label>User email <span className="text-danger">*</span></Label>
                                        <Input
                                            type="email"
                                            value={advantageForm.userEmail}
                                            onChange={(e) =>
                                                setAdvantageForm((prev) => ({
                                                    ...prev,
                                                    userEmail: e?.target?.value || "",
                                                }))
                                            }
                                            placeholder="Enter user email"
                                            disabled={advantageFetching}
                                        />
                                        {advantageErrors?.userEmail ? (
                                            <small className="text-danger d-block">
                                                {advantageErrors.userEmail}
                                            </small>
                                        ) : null}
                                    </Col>
                                </Row>

                                <Row className="mb-3">
                                    <Col md={12}>
                                        <Label>Outlook email <span className="text-danger">*</span></Label>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="flex-grow-1">
                                                <Input
                                                    type="email"
                                                    value={advantageForm.outlookEmail}
                                                    onChange={(e) =>
                                                        setAdvantageForm((prev) => ({
                                                            ...prev,
                                                            outlookEmail: e?.target?.value || "",
                                                        }))
                                                    }
                                                    placeholder="Enter outlook email"
                                                    disabled={advantageFetching}
                                                />
                                                {advantageErrors?.outlookEmail ? (
                                                    <small className="text-danger d-block">
                                                        {advantageErrors.outlookEmail}
                                                    </small>
                                                ) : null}
                                            </div>
                                            <div className="d-flex align-items-center gap-3 flex-shrink-0">
                                                <div className="form-check mb-0">
                                                    <Input
                                                        id="advantageIsOtpBased"
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        key={`advantage-is-otp-based-${advantageLoginItemId}-${Number(
                                                            !!advantageForm.isOtpBased
                                                        )}`}
                                                        defaultChecked={!!advantageForm.isOtpBased}
                                                        onChange={(e) => {
                                                            const checked = !!e?.target?.checked;
                                                            setAdvantageForm((prev) => ({
                                                                ...prev,
                                                                isOtpBased: checked,
                                                                isOld: checked ? prev.isOld : false,
                                                            }));
                                                        }}
                                                        disabled={advantageFetching}
                                                    />
                                                    <Label
                                                        className="form-check-label"
                                                        for="advantageIsOtpBased"
                                                    >
                                                        OTP based
                                                    </Label>
                                                </div>
                                                {advantageForm.isOtpBased ? (
                                                    <div className="form-check mb-0">
                                                        <Input
                                                            id="advantageIsOld"
                                                            className="form-check-input"
                                                            type="checkbox"
                                                            key={`advantage-is-old-${advantageLoginItemId}-${Number(
                                                                !!advantageForm.isOld
                                                            )}`}
                                                            defaultChecked={!!advantageForm.isOld}
                                                            onChange={(e) =>
                                                                setAdvantageForm((prev) => ({
                                                                    ...prev,
                                                                    isOld: !!e?.target?.checked,
                                                                }))
                                                            }
                                                            disabled={advantageFetching}
                                                        />
                                                        <Label
                                                            className="form-check-label"
                                                            for="advantageIsOld"
                                                        >
                                                            Is Old
                                                        </Label>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="text-end mt-4">
                                    <Button color="primary" type="submit" disabled={advantageSubmitting}>
                                        {advantageSubmitting ? "Saving..." : "Save"}
                                    </Button>
                                </div>
                            </Form>
                        </ModalBody>
                    </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

ClientRegister.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(ClientRegister);
